import { useState } from 'react';
import { useNewOperation } from '../lib/new-operation-api.js';

export interface NewOperationButtonProps {
  /** Number of operation-mode map objects currently on the board. */
  objectCount: number;
  /** Number of operation-mode arrows currently on the board. */
  arrowCount: number;
}

/**
 * Admin-only "New operation" reset. Wipes every operation-mode map object
 * and arrow, then resets the operation plan to defaults so the operation
 * starts blank. Hidden behind a confirmation modal because the action is
 * irreversible.
 */
export function NewOperationButton({
  objectCount,
  arrowCount,
}: NewOperationButtonProps): React.JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const newOperation = useNewOperation();
  const busy = newOperation.isPending;

  function onConfirm(): void {
    newOperation.mutate(undefined, {
      onSuccess: () => setConfirming(false),
    });
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setConfirming(true)}
        className="rounded bg-orange-700 px-2 py-1 text-xs font-semibold uppercase tracking-wide text-white hover:bg-orange-600"
        title="Reset the operation (admin only)"
      >
        New operation
      </button>

      {confirming && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-operation-title"
          onClick={() => {
            if (!busy) setConfirming(false);
          }}
        >
          <div
            className="w-full max-w-md rounded-lg border border-orange-700 bg-slate-900 p-5 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h2
              id="new-operation-title"
              className="mb-2 text-lg font-bold text-orange-400"
            >
              Start a new operation?
            </h2>
            <p className="mb-3 text-sm text-slate-200">
              This action is <strong className="text-orange-300">irreversible</strong>. It will:
            </p>
            <ul className="mb-4 list-inside list-disc space-y-1 text-sm text-slate-300">
              <li>
                Delete <strong className="text-orange-300">{objectCount}</strong> operation asset
                {objectCount === 1 ? '' : 's'} (no exceptions).
              </li>
              <li>
                Delete <strong className="text-orange-300">{arrowCount}</strong> operation arrow
                {arrowCount === 1 ? '' : 's'} (no exceptions).
              </li>
              <li>
                Reset the <strong className="text-orange-300">operation plan</strong> to defaults.
              </li>
            </ul>
            {newOperation.isError && (
              <p className="mb-3 rounded bg-orange-950/60 px-2 py-1 text-xs text-orange-300">
                Failed to reset operation. Please try again.
              </p>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirming(false)}
                disabled={busy}
                className="rounded bg-slate-700 px-3 py-1.5 text-sm text-slate-100 hover:bg-slate-600 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={onConfirm}
                disabled={busy}
                className="rounded bg-orange-700 px-3 py-1.5 text-sm font-semibold text-white hover:bg-orange-600 disabled:opacity-50"
              >
                {busy ? 'Resetting…' : 'Yes, start new operation'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
